import { Card, CardActions, CardContent, Divider, IconButton } from '@mui/material';
import { theme } from '@/theme.tsx';
import { Button, Stack, Tooltip, Typography } from '@components/atoms';
import { DataItem } from '@components/molecules';
import { PathNavigator } from '@utils/path-navigator.ts';
import type { FC } from 'react';
import type { ChargePoint } from '@shared/types/data.types.ts';
import { useDisconnectClient } from '@services/mutations/use-disconnect-client.mutation.ts';
import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getChargePointStatusIcon } from './EVSECard.utils.tsx';
import { Locales } from '@locales/en.ts';
import KeyIcon from '@mui/icons-material/Key';
import RestartAIcon from '@mui/icons-material/RestartAltOutlined';
import SourceAIcon from '@mui/icons-material/SourceOutlined';
import { useRestartChargePoint } from '@services/mutations/use-restart-charge-point.mutation.ts';

export interface EVSECardProps {
  cp: ChargePoint;
}

export const EVSECard: FC<EVSECardProps> = ({ cp }) => {
  const navigate = useNavigate();
  const { mutateAsync: disconnectClient, isLoading: isDisconnecting } = useDisconnectClient();
  const { mutateAsync: restartChargePoint, isLoading: isRestartingChargePoint } = useRestartChargePoint();

  const handleDisconnect = useCallback(() => void disconnectClient({ cpid: cp.cpid }), [disconnectClient, cp.cpid]);
  const handleRestart = useCallback(() => void restartChargePoint({ cpid: cp.cpid }), [restartChargePoint, cp.cpid]);
  const handleOpenDetails = useCallback(() => navigate(PathNavigator.getEVSEDetailsPath(cp.cpid)), [navigate, cp.cpid]);

  const statusIcon = getChargePointStatusIcon(cp.status);

  return (
    <Card>
      <CardContent sx={{ marginBottom: theme.spacing(2) }}>
        <Stack direction='row' alignItems='center' spacing={1}>
          <Typography variant='h6'>{cp.cpid}</Typography>
          {cp.hasBasicAuth && (
            <Tooltip title={`${Locales.labels.basicAuth}: ${cp.basic_auth_pw}`} placement='top'>
              <KeyIcon />
            </Tooltip>
          )}
          <Tooltip sx={{ ml: 'auto' }} title={`${Locales.labels.datadogLogs}`} placement='top'>
            <Link
              to={`https://app.datadoghq.com/logs?query=cpid%3D${cp.cpid}%20service%3Avirtual-evse`}
              target='_blank'
            >
              <IconButton aria-label='datadog logs'>
                <SourceAIcon />
              </IconButton>
            </Link>
          </Tooltip>
        </Stack>
        <Typography color='text.secondary' variant='caption' component='p' marginBottom={2}>
          {cp.host_name} - {cp.host_value}
        </Typography>
        <DataItem label={Locales.labels.vendor} value={cp.vendor} />
        <DataItem label={Locales.labels.model} value={cp.model} />
        <DataItem
          label={Locales.labels.status}
          value={
            <Stack direction='row' alignItems='center' spacing={0.25}>
              <span>{cp.status}</span>
              {statusIcon}
            </Stack>
          }
        />
        {cp.isRunningScenario && <DataItem label={Locales.labels.runningScenario} value={cp.running_scenario_name} />}
        <Divider sx={{ marginTop: 2 }}>{Locales.labels.ctClamp}</Divider>
        <DataItem label={Locales.labels.voltage} value={cp.ct_clamp_voltage_millivolts} unit='mV' />
        <DataItem label={Locales.labels.current} value={cp.ct_clamp_current_milliamps} unit='mA' />
      </CardContent>
      <CardActions style={{ justifyContent: 'space-between' }}>
        <Button
          size='small'
          variant='text'
          disabled={isDisconnecting}
          loading={isDisconnecting}
          onClick={handleOpenDetails}
        >
          {Locales.actions.showMore}
        </Button>
        <Stack direction='row' spacing={1}>
          <Button
            size='small'
            onClick={handleRestart}
            disabled={isRestartingChargePoint}
            loading={isRestartingChargePoint}
            startIcon={<RestartAIcon />}
          >
            {Locales.actions.restart}
          </Button>
          <Button size='small' onClick={handleDisconnect} disabled={isDisconnecting} loading={isDisconnecting}>
            {Locales.actions.stop}
          </Button>
        </Stack>
      </CardActions>
    </Card>
  );
};
